// Static illustration passes are baked once into the manor's scenery canvas.
type Room = { x: number; y: number; w: number; h: number };
const TAU = Math.PI * 2;
const palettes = [
  ["#234c47", "#122e32", "#69a88b"], ["#64483b", "#302d36", "#b79c6a"],
  ["#667780", "#243846", "#bfbc98"], ["#4b596d", "#202c40", "#a69ab7"],
  ["#5d343e", "#282838", "#c59d71"], ["#71513a", "#30333c", "#d0ae71"],
];

function oval(ctx: CanvasRenderingContext2D, x: number, y: number, rx: number, ry: number, fill: string) {
  ctx.fillStyle = fill; ctx.beginPath(); ctx.ellipse(x, y, rx, ry, 0, 0, TAU); ctx.fill();
}

function glow(ctx: CanvasRenderingContext2D, x: number, y: number, radius: number, color: string) {
  const light = ctx.createRadialGradient(x, y, 0, x, y, radius);
  light.addColorStop(0, color); light.addColorStop(1, "#00000000");
  ctx.fillStyle = light; ctx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
}

export function paintManorRoom(ctx: CanvasRenderingContext2D, room: Room, index: number) {
  const { x, y, w, h } = room;
  const [light, dark, gold] = palettes[index];
  ctx.save(); ctx.beginPath(); ctx.rect(x, y, w, h); ctx.clip();
  ctx.fillStyle = dark; ctx.fillRect(x, y, w, h);
  const wooden = [1, 4, 5].includes(index);
  if (wooden) {
    // Individual staggered oak boards, with grain and bevels.
    for (let row = 0; row < h / 20; row++) {
      for (let column = -1; column < w / 90; column++) {
        const px = x + column * 90 + (row % 2) * 45; const py = y + row * 20;
        const shade = .12 + ((row * 13 + column * 7 + 100) % 9) * .028;
        ctx.fillStyle = light; ctx.globalAlpha = .65 + shade; ctx.fillRect(px + 1, py + 1, 88, 18);
        ctx.globalAlpha = 1; ctx.strokeStyle = "#e4c38918"; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(px + 2, py + 2); ctx.lineTo(px + 88, py + 2); ctx.stroke();
        for (let grain = 0; grain < 3; grain++) {
          ctx.strokeStyle = "#070e1830";
          ctx.beginPath(); ctx.moveTo(px + 5, py + 5 + grain * 4);
          ctx.bezierCurveTo(px + 20, py + grain * 4 + 2, px + 50, py + grain * 4 + 10, px + 83, py + 5 + grain * 4); ctx.stroke();
        }
      }
    }
  } else {
    // Diamond stone tiles with softly shaded faces and inset brass joints.
    ctx.save(); ctx.translate(x + w / 2, y + h / 2); ctx.rotate(Math.PI / 4);
    for (let row = -12; row < 12; row++) for (let column = -12; column < 12; column++) {
      const px = column * 34; const py = row * 34;
      const tile = ctx.createLinearGradient(px, py, px + 34, py + 34);
      tile.addColorStop(0, (row + column) % 2 ? light : dark);
      tile.addColorStop(1, (row + column) % 2 ? dark : "#16232f");
      ctx.fillStyle = tile; ctx.fillRect(px + 1, py + 1, 32, 32);
      ctx.strokeStyle = "#dacda325"; ctx.strokeRect(px + 2, py + 2, 30, 30);
    }
    ctx.restore();
  }

  // Room-specific centerpieces are inlaid in the floor, never new obstacles.
  if (index === 0 || index === 2) {
    const cx = x + w / 2; const cy = y + h * .51;
    oval(ctx, cx, cy + 3, 114, 126, "#06172565");
    oval(ctx, cx, cy, 106, 118, index === 0 ? "#163d36" : "#304455");
    ctx.strokeStyle = gold; ctx.lineWidth = 3;
    ctx.beginPath(); ctx.ellipse(cx, cy, 102, 114, 0, 0, TAU); ctx.stroke();
    ctx.lineWidth = 1; ctx.beginPath(); ctx.ellipse(cx, cy, 94, 106, 0, 0, TAU); ctx.stroke();
    for (let petal = 0; petal < 12; petal++) {
      const angle = petal * TAU / 12;
      ctx.save(); ctx.translate(cx, cy); ctx.rotate(angle);
      ctx.fillStyle = petal % 2 ? "#baa87375" : "#7eaaa57a";
      ctx.beginPath(); ctx.moveTo(0, -82); ctx.quadraticCurveTo(27, -32, 0, -10); ctx.quadraticCurveTo(-27, -32, 0, -82); ctx.fill(); ctx.restore();
    }
    oval(ctx, cx, cy, 13, 13, gold);
  } else {
    const rx = x + 52; const ry = y + 113; const rw = w - 104; const rh = h - 206;
    const rugColors = ["", "#45394e", "", "#344c6d", "#7a3249", "#65503a"];
    const velvet = ctx.createRadialGradient(rx + rw / 2, ry + rh / 2, 10, rx + rw / 2, ry + rh / 2, 180);
    velvet.addColorStop(0, rugColors[index]); velvet.addColorStop(1, "#171d2b");
    ctx.shadowColor = "#020913aa"; ctx.shadowBlur = 12; ctx.shadowOffsetY = 5;
    ctx.fillStyle = velvet; ctx.beginPath(); ctx.roundRect(rx, ry, rw, rh, index === 4 ? 28 : 5); ctx.fill();
    ctx.shadowBlur = 0; ctx.shadowOffsetY = 0;
    for (const inset of [6, 10, 22]) { ctx.strokeStyle = inset === 10 ? gold : "#baa67475"; ctx.lineWidth = inset === 10 ? 2 : 1; ctx.strokeRect(rx + inset, ry + inset, rw - inset * 2, rh - inset * 2); }
    // Repeating embroidered flourishes and a gold center medallion.
    for (let ryOffset = 34; ryOffset < rh - 20; ryOffset += 28) {
      for (let rxOffset = 33; rxOffset < rw - 20; rxOffset += 28) {
        ctx.strokeStyle = "#dfc88b30"; ctx.beginPath(); ctx.ellipse(rx + rxOffset, ry + ryOffset, 4, 7, Math.PI / 4, 0, TAU); ctx.stroke();
      }
    }
    const cx = rx + rw / 2; const cy = ry + rh / 2;
    ctx.fillStyle = "#121e3266"; ctx.strokeStyle = gold; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(cx, cy - 66); ctx.bezierCurveTo(cx + 18, cy - 28, cx + 50, cy - 28, cx + 53, cy); ctx.bezierCurveTo(cx + 28, cy + 19, cx + 18, cy + 28, cx, cy + 66); ctx.bezierCurveTo(cx - 18, cy + 28, cx - 50, cy + 28, cx - 53, cy); ctx.bezierCurveTo(cx - 28, cy - 19, cx - 18, cy - 28, cx, cy - 66); ctx.fill(); ctx.stroke();
    for (let thread = 8; thread < rw - 8; thread += 5) { ctx.strokeStyle = "#d2b98b90"; ctx.beginPath(); ctx.moveTo(rx + thread, ry - 5); ctx.lineTo(rx + thread, ry); ctx.moveTo(rx + thread, ry + rh); ctx.lineTo(rx + thread, ry + rh + 5); ctx.stroke(); }
  }

  // Architectural shadow and raised wall panelling stay within the room edge.
  const shade = ctx.createLinearGradient(x, y, x, y + 64);
  shade.addColorStop(0, "#020711ce"); shade.addColorStop(1, "#02071100");
  ctx.fillStyle = shade; ctx.fillRect(x, y, w, 64);
  ctx.strokeStyle = "#c4ae7c66"; ctx.lineWidth = 2; ctx.strokeRect(x + 9, y + 9, w - 18, h - 18);
  for (let panel = 18; panel < w - 24; panel += 41) {
    ctx.fillStyle = "#172533"; ctx.fillRect(x + panel, y + 3, 35, 17);
    ctx.strokeStyle = "#ddbd7d55"; ctx.strokeRect(x + panel + 3, y + 5, 29, 10);
  }
  const outerX = index % 2 === 0 ? x + 4 : x + w - 22;
  for (const windowY of [y + 54, y + h - 121]) paintWindow(ctx, outerX, windowY, index % 2 === 0 ? 1 : -1);

  // Gilded wall portraits, each with a different painted silhouette.
  for (let portrait = 0; portrait < (index === 3 ? 4 : 2); portrait++) {
    const px = x + 46 + portrait * (index === 3 ? 79 : 235); const py = y + 26;
    ctx.fillStyle = "#050b15aa"; ctx.fillRect(px - 2, py + 4, 31, 45);
    const frame = ctx.createLinearGradient(px, py, px + 28, py + 42);
    frame.addColorStop(0, "#f1d491"); frame.addColorStop(.45, "#6c5135"); frame.addColorStop(1, "#bca36c");
    ctx.fillStyle = frame; ctx.fillRect(px, py, 28, 41);
    ctx.fillStyle = ["#273b49", "#43313f", "#29433d"][(index + portrait) % 3]; ctx.fillRect(px + 4, py + 4, 20, 33);
    oval(ctx, px + 14, py + 30, 8, 8, "#101822"); oval(ctx, px + 14, py + 17, 5, 7, "#c3ad87");
    ctx.fillStyle = "#2a2630"; ctx.fillRect(px + 8, py + 8, 12, 5);
  }
  if (index === 0) {
    // Hanging ivy is attached to the perimeter; the paths below stay clear.
    for (const side of [x + 22, x + w - 22]) {
      for (let leaf = 0; leaf < 16; leaf++) {
        const ly = y + 25 + leaf * 19; const lx = side + Math.sin(leaf * 1.8) * 8;
        ctx.strokeStyle = "#749865"; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(side, ly - 18); ctx.lineTo(lx, ly + 8); ctx.stroke();
        ctx.save(); ctx.translate(lx, ly); ctx.rotate(leaf * 1.8); oval(ctx, 4, 0, 11, 5, leaf % 2 ? "#4d8e66" : "#2b674d"); ctx.restore();
      }
    }
  }
  // Cool window spill and amber sconce light create a warm/cool contrast.
  glow(ctx, outerX, y + 110, 220, "#8bcbe231");
  glow(ctx, x + w / 2, y + 24, 160, "#ffc56b3f");
  ctx.restore();
}

function paintWindow(ctx: CanvasRenderingContext2D, x: number, y: number, direction: number) {
  ctx.save();
  const light = ctx.createLinearGradient(x, y, x + direction * 230, y + 150);
  light.addColorStop(0, "#c3edff45"); light.addColorStop(1, "#99d7ff00");
  ctx.fillStyle = light;
  ctx.beginPath(); ctx.moveTo(x + 9, y); ctx.lineTo(x + 9, y + 73); ctx.lineTo(x + direction * 230, y + 200); ctx.lineTo(x + direction * 195, y + 100); ctx.closePath(); ctx.fill();
  ctx.fillStyle = "#070d1a"; ctx.strokeStyle = "#d2bb87"; ctx.lineWidth = 3;
  ctx.beginPath(); ctx.roundRect(x - 2, y - 2, 23, 80, 10); ctx.fill(); ctx.stroke();
  const glass = ctx.createLinearGradient(x, y, x + 18, y + 73);
  glass.addColorStop(0, "#dcf6ff"); glass.addColorStop(.35, "#70baca"); glass.addColorStop(1, "#263c68");
  ctx.fillStyle = glass; ctx.fillRect(x + 3, y + 7, 12, 61);
  ctx.strokeStyle = "#263d56"; ctx.lineWidth = 2;
  for (let bar = 14; bar < 69; bar += 15) { ctx.beginPath(); ctx.moveTo(x + 3, y + bar); ctx.lineTo(x + 15, y + bar + 9); ctx.moveTo(x + 15, y + bar); ctx.lineTo(x + 3, y + bar + 9); ctx.stroke(); }
  ctx.fillStyle = "#aa946d"; ctx.fillRect(x - 5, y + 76, 29, 5);
  ctx.restore();
}

export function paintSconceFlames(ctx: CanvasRenderingContext2D, time: number) {
  ctx.save();
  for (const y of [124, 595, 1080]) for (const x of [256, 644]) {
    const flicker = Math.sin(time * 7 + x) * 1.5;
    ctx.strokeStyle = "#bda16d"; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(x - 15, y - 3); ctx.quadraticCurveTo(x, y + 20, x + 15, y - 3); ctx.stroke();
    for (const dx of [-15, 0, 15]) {
      const fy = y - (dx === 0 ? 12 : 6);
      ctx.fillStyle = "#deceb2"; ctx.fillRect(x + dx - 2, fy, 4, 12);
      oval(ctx, x + dx + flicker * .5, fy - 5, 3 + flicker * .2, 6, "#ffb952");
      oval(ctx, x + dx, fy - 3, 1.5, 3, "#fff1c5");
    }
  }
  ctx.restore();
}
